import type { MintRunColour } from "./types";

// Fixed saturation and lightness so a created run sits in the same register as
// the ones the estimation drew — only the hue is minted.
const SATURATION = 0.62;
const LIGHTNESS = 0.45;

const hueOf = (circuitId: string): number => {
  let hash = 0;
  for (let i = 0; i < circuitId.length; i += 1) {
    hash = (hash * 31 + circuitId.charCodeAt(i)) % 360;
  }
  // Golden-angle walk: two runs created one after the other land far apart on
  // the wheel instead of one shade of the same colour.
  return (hash * 137.508) % 360;
};

const channel = (p: number, q: number, t: number): number => {
  const shifted = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
  if (shifted < 1 / 6) return p + (q - p) * 6 * shifted;
  if (shifted < 1 / 2) return q;
  if (shifted < 2 / 3) return p + (q - p) * (2 / 3 - shifted) * 6;
  return p;
};

/**
 * A colour of its own for a run he created.
 *
 * Without it the first device moved onto a created run keeps the colour of the
 * run it LEFT — two runs drawn identically on a sheet he navigates by colour,
 * which is the one thing the palette is for.
 */
export const mintRunColour: MintRunColour = ({ circuitId }) => {
  const h = hueOf(circuitId) / 360;
  const q = LIGHTNESS + SATURATION * Math.min(LIGHTNESS, 1 - LIGHTNESS);
  const p = 2 * LIGHTNESS - q;
  return [
    Math.round(channel(p, q, h + 1 / 3) * 255),
    Math.round(channel(p, q, h) * 255),
    Math.round(channel(p, q, h - 1 / 3) * 255),
  ];
};
